import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientClinicalScoreDto, patientClinicalScoreFormData } from "../types/patientClinicalScore";
import { patientClinicalScoreService } from "../services/patientClinicalScoreService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientClinicalScore = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: clinicalScores = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientClinicalScoreDto[]>(
    patientId ? `patient-clinical-scores-${patientId}` : "patient-clinical-scores-all",
    async (signal) => {
      if (patientId) {
        return patientClinicalScoreService.getByPatientId(patientId, signal);
      }
      return patientClinicalScoreService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload αν υπάρχει)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newScore: patientClinicalScoreFormData) => {
    const payload = patientId 
      ? { ...newScore, patientId: Number(patientId) } 
      : newScore;
      
    return patientClinicalScoreService.create(payload as any);
  });

  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: patientClinicalScoreFormData }) => {
    return patientClinicalScoreService.update(id, data as any);
  });
  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientClinicalScoreService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientClinicalScore = async (newScore: patientClinicalScoreFormData): Promise<void> => {
    try {
      await createExecution(newScore);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το κλινικό score αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας κλινικού score";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientClinicalScore = async (id: number, updatedData: patientClinicalScoreFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης κλινικού score";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };
  const deletePatientClinicalScores = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientClinicalScores: clinicalScores, // Τα δεδομένα (είτε για έναν ασθενή, είτε όλα)
    allClinicalScores: clinicalScores,     // Alias για συμβατότητα
    isLoading: isFetching || isCreating || isDeleting,
    error: getError || createError || deleteError,
    fetchPatientClinicalScores: () => refetch(true),
    fetchClinicalScores: () => refetch(true),
    createPatientClinicalScore,
    updatePatientClinicalScore,
    deletePatientClinicalScores,
    modalState,
    closeModal,
  };
};